import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { ChannelModal } from '../components/ChannelModal';
import { pressReleaseChannelsApi } from '../pressReleaseApi';
import { channelPath, PUBLIC_RELATIONS_SETTINGS_PATH, type PressReleaseChannelListItem } from '../pressReleaseTypes';

/**
 * Where a press release can be posted — reached through Public Relations › Settings, never as a
 * sidebar row of its own. Every live channel is a line on every published release's checklist.
 */
export function ChannelsIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [editing, setEditing] = useState<PressReleaseChannelListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; channel: PressReleaseChannelListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchChannels = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      pressReleaseChannelsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (channel: PressReleaseChannelListItem): MenuItem[] => [
      {
        key: `restore-${channel.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'press-release-channels.delete',
        disabledReason: channel.isActive ? 'This channel is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', channel }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await pressReleaseChannelsApi.remove(confirming.channel.id);
      else await pressReleaseChannelsApi.restore(confirming.channel.id);

      toast.success(`${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.channel.name}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PressReleaseChannelListItem>
        module="press-release-channels"
        moduleNoun="channels"
        breadcrumb={['Public Relations', { label: 'Settings', to: PUBLIC_RELATIONS_SETTINGS_PATH }, 'Channels']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['PressReleaseChannel']}
        rowKey={(channel) => channel.id}
        fetch={fetchChannels}
        reloadKey={reloadKey}
        searchPlaceholder="Search channels"
        emptyMessage="No channels yet. Add where press releases are posted — Facebook, Website, Instagram."
        onCreate={() => setCreating(true)}
        createPermission="press-release-channels.create"
        onRowOpen={(channel) => navigate(channelPath(channel.id))}
        openPermission="press-release-channels.read"
        rowActions={rowActions}
        onEdit={(channel) => setEditing(channel)}
        editPermission="press-release-channels.update"
        onDelete={(channel) => setConfirming({ kind: 'delete', channel })}
        deletePermission="press-release-channels.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(channel) => (channel.isActive ? null : 'This channel is already deleted.')}
        onClearData={() => pressReleaseChannelsApi.clear()}
        clearDataPermission="press-release-channels.clear-data"
        otherInfo={(channel) => channel}
      />

      {creating || editing !== null ? (
        <ChannelModal
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete channel' : 'Restore channel'}
          danger={confirming.kind === 'delete'}
          requireConfirm
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`press-release-channel-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `"${confirming.channel.name}" leaves every press release's checklist.`
                  : `"${confirming.channel.name}" is back on every published press release's checklist.`}
              </p>

              {confirming.kind === 'delete' && confirming.channel.postedCount > 0 ? (
                <p>
                  {confirming.channel.postedCount} press {confirming.channel.postedCount === 1 ? 'release is' : 'releases are'} ticked
                  as posted on it. Those ticks are kept and still shown, but it cannot be ticked afresh.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted channel can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<PressReleaseChannelListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 240,
    flex: true,
    sortable: true,
    render: (channel) => <TextCell value={channel.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (channel) => <TextCell value={channel.description} />,
  },
  {
    // A correlated count — not sortable.
    key: 'postedCount',
    header: 'Posted',
    width: 100,
    align: 'right',
    summary: true,
    render: (channel) => <TextCell value={String(channel.postedCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (channel) =>
      channel.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
